/**
 * Ground markers for enemy attacks being wound up, and the bolts enemies fire.
 *
 * Each telegraph is an outline of where the attack will land plus a fill that
 * grows from its origin as the windup runs out, so the player can read both
 * where to stand and when to move. A mend is drawn the same way in green, as
 * a ring on the ally being restored; a rally in gold, ringing the allies it
 * reaches; a blink in violet, where the caster will reappear. Burning ground
 * an attack leaves is a glowing disc that fades as it burns out. A held door
 * (The Held Door) is drawn in Sorrow's blue: a line across the doorway that
 * fills while its caster holds the hotkey and empties as it runs out, and a
 * ring under each enemy waiting at it that empties as its wait runs out.
 *
 * An attack whose spot is committed (a leap leaving the ground, a charge
 * setting off down its lane) flashes brighter as it locks: `lockFlash`
 * runs from 1 at the lock down to 0, and the whole shape is overlaid with a
 * brighter red at that strength.
 *
 * Shapes come in tile units (see `NpcAttackShape`) and are built flat in a
 * local frame with +X as "forward", then yawed onto the attack's direction.
 */

import {
    Color3,
    Mesh,
    MeshBuilder,
    StandardMaterial,
    TransformNode,
    VertexData,
    type Scene,
} from '@babylonjs/core';
import type { NpcAttackShape, Vector2 } from '@sorrem/shared';
import { worldSpaceToScenePosition } from '../world-to-scene-position';
import { RALLY_COLOR_HEX } from './rally-pulse-system';
import { TILE_UNIT_SIZE } from '../scale-constants';
import { reconcileKeyedEffects } from './keyed-effects';

/** How a telegraph is colored: an attack, a mend, a rally, a blink's landing, or a held door. */
export type TelegraphRenderKind = 'attack' | 'mend' | 'rally' | 'blink' | 'held-door';

export interface TelegraphRenderState {
    readonly id: string;
    readonly kind: TelegraphRenderKind;
    readonly shape: NpcAttackShape;
    /** How far its attack reaches, in tiles: an arc's radius, which its shape does not give. */
    readonly reachTiles?: number;
    /** Tile position the shape starts from. */
    readonly origin: Vector2;
    /** Unit vector, in tiles, the shape points along. */
    readonly direction: Vector2;
    /** 0 when the windup starts, 1 when it lands. */
    readonly progress: number;
    /** The lock flash: 1 the moment the attack commits, fading to 0. Absent or 0 draws no flash. */
    readonly lockFlash?: number;
}

/** A ring on the ground under an elite or a guardian, so it stands out from its pack. */
export interface AuraRenderState {
    readonly id: string;
    /** Tile position. */
    readonly position: Vector2;
    readonly colorHex: string;
    /** In tiles. */
    readonly radius: number;
}

export interface ProjectileRenderState {
    readonly id: string;
    /** Tile position. */
    readonly position: Vector2;
    /** Its body radius, in tiles. */
    readonly radius: number;
    /** How high it flies, in tiles: a lob on its arc; a bolt at chest height when omitted. */
    readonly height?: number;
}

/** Height of the markers above the floor, enough to clear it without floating. */
const MARKER_HEIGHT = 0.04;
/** A bolt flies at about chest height, in tiles. */
const PROJECTILE_HEIGHT = 1;
/** An aim line is never drawn longer than this, so a long-range bolt does not paint the whole room. */
const MAX_AIM_LINE_TILES = 8;
/** An arc's reach when its telegraph gives none. */
const DEFAULT_ARC_RADIUS_TILES = 1.5;
const SECTOR_SEGMENTS = 40;
/** How thick an elite's aura ring is. */
const AURA_THICKNESS_TILES = 0.06;

const ATTACK_COLOR = Color3.FromHexString('#e0412f');
/** A locked attack flashes in a brighter red: still a flat matte telegraph, never a glow. */
const LOCK_FLASH_COLOR = Color3.FromHexString('#ff7a5c');
const LOCK_FLASH_ALPHA = 0.7;
const MEND_COLOR = Color3.FromHexString('#4fd18b');
const RALLY_COLOR = Color3.FromHexString(RALLY_COLOR_HEX);
const BLINK_COLOR = Color3.FromHexString('#9b6bff');
/** Sorrow's essence color (the client's `ESSENCE_COLORS.sorrow`). */
const HELD_DOOR_COLOR = Color3.FromHexString('#2f6bff');
const PROJECTILE_COLOR = new Color3(1, 0.45, 0.15);

interface TelegraphEntry {
    readonly root: TransformNode;
    readonly outline: Mesh;
    readonly fill: Mesh;
    /** The lock flash over the whole shape, built the first time it locks. */
    flash: Mesh | null;
    /** Builds its shape again, for the lock flash. */
    readonly buildShape: (suffix: string) => Mesh;
    /** Whether the fill grows radially (circle, arc) or along its length (line, aim line). */
    readonly isRadial: boolean;
}

/** A flat fan in the local XZ plane, centered on the origin and pointing along +X. */
function createSectorMesh(name: string, radius: number, degrees: number, scene: Scene): Mesh {
    const half = (Math.min(360, Math.max(1, degrees)) * Math.PI) / 360;
    const positions: number[] = [0, 0, 0];
    const indices: number[] = [];
    for (let segment = 0; segment <= SECTOR_SEGMENTS; segment += 1) {
        const angle = -half + (2 * half * segment) / SECTOR_SEGMENTS;
        positions.push(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
        if (segment > 0) {
            indices.push(0, segment, segment + 1);
        }
    }
    return createFlatMesh(name, positions, indices, scene);
}

/** A flat strip in the local XZ plane from the origin out along +X. */
function createStripMesh(name: string, length: number, width: number, scene: Scene): Mesh {
    const halfWidth = width / 2;
    const positions = [0, 0, -halfWidth, length, 0, -halfWidth, length, 0, halfWidth, 0, 0, halfWidth];
    return createFlatMesh(name, positions, [0, 1, 2, 0, 2, 3], scene);
}

function createFlatMesh(name: string, positions: number[], indices: number[], scene: Scene): Mesh {
    const mesh = new Mesh(name, scene);
    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.normals = positions.map((_, index) => (index % 3 === 1 ? 1 : 0));
    vertexData.applyToMesh(mesh);
    mesh.isPickable = false;
    return mesh;
}

/** The yaw that turns local +X onto a tile-space direction (tile X is scene Z, tile Y is scene X; Babylon is left-handed). */
export function resolveTelegraphYaw(direction: Vector2): number {
    const sceneX = direction.y;
    const sceneZ = direction.x;
    return Math.atan2(-sceneZ, sceneX);
}

export class TelegraphSystem {
    private readonly telegraphs = new Map<string, TelegraphEntry>();
    private readonly projectiles = new Map<string, Mesh>();
    private readonly auras = new Map<string, { readonly mesh: Mesh; readonly material: StandardMaterial; readonly key: string }>();
    private readonly outlineMaterials: Record<TelegraphRenderKind, StandardMaterial>;
    private readonly fillMaterials: Record<TelegraphRenderKind, StandardMaterial>;
    private readonly projectileMaterial: StandardMaterial;
    private readonly lockFlashMaterial: StandardMaterial;

    public constructor(private readonly scene: Scene, private readonly tileUnitSize = TILE_UNIT_SIZE) {
        this.outlineMaterials = {
            attack: this.createMaterial('telegraph-outline-attack', ATTACK_COLOR, 0.22),
            mend: this.createMaterial('telegraph-outline-mend', MEND_COLOR, 0.22),
            rally: this.createMaterial('telegraph-outline-rally', RALLY_COLOR, 0.22),
            blink: this.createMaterial('telegraph-outline-blink', BLINK_COLOR, 0.22),
            'held-door': this.createMaterial('telegraph-outline-held-door', HELD_DOOR_COLOR, 0.3),
        };
        this.fillMaterials = {
            attack: this.createMaterial('telegraph-fill-attack', ATTACK_COLOR, 0.45),
            mend: this.createMaterial('telegraph-fill-mend', MEND_COLOR, 0.45),
            rally: this.createMaterial('telegraph-fill-rally', RALLY_COLOR, 0.3),
            blink: this.createMaterial('telegraph-fill-blink', BLINK_COLOR, 0.45),
            'held-door': this.createMaterial('telegraph-fill-held-door', HELD_DOOR_COLOR, 0.55),
        };
        this.projectileMaterial = this.createMaterial('npc-projectile', PROJECTILE_COLOR, 1);
        this.lockFlashMaterial = this.createMaterial('telegraph-lock-flash', LOCK_FLASH_COLOR, LOCK_FLASH_ALPHA);
    }

    /** Shows exactly these telegraphs: new ones are built, gone ones disposed, the rest updated. */
    public setTelegraphs(states: readonly TelegraphRenderState[]): void {
        reconcileKeyedEffects(this.telegraphs, states, {
            create: (state) => this.createTelegraph(state),
            apply: (entry, state) => {
                const progress = Math.min(1, Math.max(0, state.progress));
                if (entry.isRadial) {
                    entry.fill.scaling.set(Math.max(0.001, progress), 1, Math.max(0.001, progress));
                } else {
                    entry.fill.scaling.set(Math.max(0.001, progress), 1, 1);
                }
                this.applyLockFlash(entry, state.lockFlash ?? 0);
            },
            unseen: () => 'remove',
            discard: (entry) => entry.root.dispose(false, false),
        });
    }

    /** Shows exactly these projectiles (bolts and lobs) at their current positions. */
    public setProjectiles(states: readonly ProjectileRenderState[]): void {
        reconcileKeyedEffects(this.projectiles, states, {
            create: (state) => {
                const mesh = MeshBuilder.CreateSphere(`projectile-${state.id}`, { diameter: state.radius * 2 * this.tileUnitSize, segments: 8 }, this.scene);
                mesh.material = this.projectileMaterial;
                mesh.isPickable = false;
                return mesh;
            },
            apply: (mesh, state) => {
                const position = worldSpaceToScenePosition(state.position, this.tileUnitSize, (state.height ?? PROJECTILE_HEIGHT) * this.tileUnitSize);
                mesh.position.set(position.x, position.y, position.z);
            },
            unseen: () => 'remove',
            discard: (mesh) => mesh.dispose(),
        });
    }

    /** Shows exactly these auras, following their enemies. */
    public setAuras(states: readonly AuraRenderState[]): void {
        const keyOf = (state: AuraRenderState): string => `${state.colorHex}:${state.radius}`;
        reconcileKeyedEffects(this.auras, states, {
            create: (state) => {
                const mesh = MeshBuilder.CreateTorus(`aura-${state.id}`, {
                    diameter: state.radius * 2 * this.tileUnitSize,
                    thickness: AURA_THICKNESS_TILES * this.tileUnitSize,
                    tessellation: 40,
                }, this.scene);
                const material = this.createMaterial(`aura-${state.id}`, Color3.FromHexString(state.colorHex), 0.7);
                mesh.material = material;
                mesh.isPickable = false;
                return { mesh, material, key: keyOf(state) };
            },
            // Its ring is built in its color and size.
            isStale: (aura, state) => aura.key !== keyOf(state),
            apply: (aura, state) => {
                const position = worldSpaceToScenePosition(state.position, this.tileUnitSize, MARKER_HEIGHT);
                aura.mesh.position.set(position.x, position.y, position.z);
            },
            unseen: () => 'remove',
            discard: (aura) => {
                aura.mesh.dispose();
                aura.material.dispose();
            },
        });
    }

    public dispose(): void {
        this.setTelegraphs([]);
        this.setProjectiles([]);
        this.setAuras([]);
        for (const material of [...Object.values(this.outlineMaterials), ...Object.values(this.fillMaterials), this.projectileMaterial, this.lockFlashMaterial]) {
            material.dispose();
        }
    }

    private createMaterial(name: string, color: Color3, alpha: number): StandardMaterial {
        const material = new StandardMaterial(name, this.scene);
        material.diffuseColor = Color3.Black();
        material.specularColor = Color3.Black();
        material.emissiveColor = color;
        material.disableLighting = true;
        material.alpha = alpha;
        material.backFaceCulling = false;
        // Drawn over the floor it lies on.
        material.zOffset = -2;
        return material;
    }

    /** Overlays the whole shape at the flash's strength (mesh visibility scales the shared material's alpha). */
    private applyLockFlash(entry: TelegraphEntry, lockFlash: number): void {
        const strength = Math.min(1, Math.max(0, lockFlash));
        if (strength <= 0) {
            entry.flash?.setEnabled(false);
            return;
        }
        if (!entry.flash) {
            entry.flash = entry.buildShape('flash');
            entry.flash.material = this.lockFlashMaterial;
            entry.flash.parent = entry.root;
            entry.flash.position.y = 0.01;
        }
        entry.flash.setEnabled(true);
        entry.flash.visibility = strength;
    }

    private createTelegraph(state: TelegraphRenderState): TelegraphEntry {
        const root = new TransformNode(`telegraph-${state.id}`, this.scene);
        const origin = worldSpaceToScenePosition(state.origin, this.tileUnitSize, MARKER_HEIGHT);
        root.position.set(origin.x, origin.y, origin.z);
        root.rotation.y = resolveTelegraphYaw(state.direction);

        const unit = this.tileUnitSize;
        const build = (suffix: string): { mesh: Mesh; isRadial: boolean } => {
            const name = `telegraph-${suffix}-${state.id}`;
            const shape = state.shape;
            switch (shape.kind) {
                // A leap and a lob are drawn where they come down.
                case 'circle':
                case 'leap':
                case 'lob':
                    return { mesh: createSectorMesh(name, shape.radius * unit, 360, this.scene), isRadial: true };
                case 'arc':
                    return { mesh: createSectorMesh(name, (state.reachTiles ?? DEFAULT_ARC_RADIUS_TILES) * unit, shape.degrees, this.scene), isRadial: true };
                case 'line':
                    return { mesh: createStripMesh(name, shape.length * unit, shape.width * unit, this.scene), isRadial: false };
                case 'projectile':
                    return {
                        mesh: createStripMesh(name, Math.min(shape.maxDistance, MAX_AIM_LINE_TILES) * unit, shape.radius * 2 * unit, this.scene),
                        isRadial: false,
                    };
            }
        };

        const outline = build('outline');
        const fill = build('fill');
        outline.mesh.material = this.outlineMaterials[state.kind];
        fill.mesh.material = this.fillMaterials[state.kind];
        outline.mesh.parent = root;
        fill.mesh.parent = root;
        // The fill sits a hair above the outline so the two never fight.
        fill.mesh.position.y = 0.005;
        return { root, outline: outline.mesh, fill: fill.mesh, flash: null, buildShape: (suffix) => build(suffix).mesh, isRadial: outline.isRadial };
    }
}
